import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserCheck,
  UserX,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { adminApi, AdminUser } from "../../lib/adminApi";
import { notify } from "../../lib/notify";

type SortField = "email" | "name" | "createdAt" | "lastLoginAt";
type SortOrder = "asc" | "desc";

function fmt(d?: string) {
  return d ? new Date(d).toLocaleDateString() : "—";
}

function SortIcon({ field, sortField, sortOrder }: { field: SortField; sortField: SortField; sortOrder: SortOrder }) {
  if (sortField !== field) return <ChevronsUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  return sortOrder === "asc"
    ? <ChevronUp className="ml-1 inline h-3 w-3" />
    : <ChevronDown className="ml-1 inline h-3 w-3" />;
}

export default function AdminUsersPage() {
  const navigate = useNavigate();

  // List state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [loading, setLoading] = useState(true);

  // Bulk select
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Inline action
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState("");
  const [createName, setCreateName] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState<"user" | "admin">("user");
  const [createLoading, setCreateLoading] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Delete dialog
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = (p = page, lim = limit) => {
    setLoading(true);
    adminApi
      .listUsers({
        page: p,
        limit: lim,
        search: search || undefined,
        status: statusFilter || undefined,
        role: roleFilter || undefined,
        sort: sortField,
        order: sortOrder,
      })
      .then((res) => {
        setUsers(res.users);
        setTotal(res.total);
        setSelected(new Set());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1, limit); setPage(1); }, [search, statusFilter, roleFilter, sortField, sortOrder, limit]); // eslint-disable-line

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  // Inline user update
  const handleUpdate = async (
    userId: string,
    body: { role?: "user" | "admin"; accountStatus?: "active" | "suspended" },
    msg: string
  ) => {
    setActionLoading(userId);
    try {
      await adminApi.updateUser(userId, body);
      notify.success(msg);
      load();
    } catch (err: any) {
      notify.error(err?.message ?? "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk actions
  const handleBulkUpdate = async (body: { accountStatus: "active" | "suspended" }, msg: string) => {
    setBulkLoading(true);
    try {
      await Promise.all(Array.from(selected).map((id) => adminApi.updateUser(id, body)));
      notify.success(msg);
      load();
    } catch {
      notify.error("Some actions failed");
    } finally {
      setBulkLoading(false);
    }
  };

  // Create user
  const handleCreateUser = async () => {
    setCreateLoading(true);
    try {
      await adminApi.createUser({ email: createEmail, name: createName || undefined, password: createPassword, role: createRole });
      notify.success(`User ${createEmail} created`);
      setCreateOpen(false);
      setCreateEmail(""); setCreateName(""); setCreatePassword(""); setCreateRole("user");
      load(1);
      setPage(1);
    } catch (err: any) {
      notify.error(err?.response?.data?.error ?? err?.message ?? "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  // Edit save
  const handleEditSave = async () => {
    if (!editUser) return;
    setEditLoading(true);
    try {
      await adminApi.updateUser(editUser.id, { name: editName, email: editEmail });
      notify.success("User updated");
      setEditUser(null);
      load();
    } catch (err: any) {
      notify.error(err?.message ?? "Update failed");
    } finally {
      setEditLoading(false);
    }
  };

  // Delete confirm
  const handleDeleteConfirm = async () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    try {
      await adminApi.deleteUser(deleteUser.id);
      notify.success("User deleted");
      setDeleteUser(null);
      setUsers((prev) => prev.filter((u) => u.id !== deleteUser.id));
      setTotal((t) => t - 1);
    } catch (err: any) {
      notify.error(err?.message ?? "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const allSelected = users.length > 0 && users.every((u) => selected.has(u.id));
  const someSelected = selected.size > 0;
  const totalPages = Math.ceil(total / limit);

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map((u) => u.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h2 tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} total users</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />Add User
        </Button>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 rounded-md bg-muted/40 px-4 py-2.5 text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <Button size="sm" variant="outline" disabled={bulkLoading}
            onClick={() => handleBulkUpdate({ accountStatus: "active" }, `${selected.size} users activated`)}>
            <UserCheck className="mr-1.5 h-3.5 w-3.5" />Activate
          </Button>
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={bulkLoading}
            onClick={() => handleBulkUpdate({ accountStatus: "suspended" }, `${selected.size} users suspended`)}>
            <UserX className="mr-1.5 h-3.5 w-3.5" />Suspend
          </Button>
          <Button size="sm" variant="ghost" className="ml-auto text-muted-foreground"
            onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-64 h-9"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md bg-background px-3 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="h-9 rounded-md bg-background px-3 text-sm">
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
          className="h-9 rounded-md bg-background px-3 text-sm ml-auto">
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-md bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="w-10 px-3 py-3">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  className="rounded border-border" />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("email")}>
                Email <SortIcon field="email" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("name")}>
                Name <SortIcon field="name" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-left font-medium">Role</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Projects</th>
              <th className="px-4 py-3 text-left font-medium">Services</th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("createdAt")}>
                Joined <SortIcon field="createdAt" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("lastLoginAt")}>
                Last Login <SortIcon field="lastLoginAt" sortField={sortField} sortOrder={sortOrder} />
              </th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              : users.map((u) => (
                  <tr key={u.id}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => navigate(`/admin/users/${u.id}`)}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)}
                        className="rounded border-border" />
                    </td>
                    <td className="px-4 py-3 font-medium">{u.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.name || "—"}</td>
                    <td className="px-4 py-3">
                      {u.role === "admin"
                        ? <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">Admin</Badge>
                        : <Badge variant="outline" className="text-xs">User</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline"
                        className={u.accountStatus === "active" ? "text-success border-success/30 text-xs" : "text-muted-foreground text-xs"}>
                        {u.accountStatus ?? "active"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.projectCount ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.serviceCount ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(u.createdAt)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.lastLoginAt ? fmt(u.lastLoginAt) : "Never"}</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {/* Edit */}
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          title="Edit" disabled={actionLoading === u.id}
                          onClick={() => { setEditUser(u); setEditName(u.name ?? ""); setEditEmail(u.email); }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {/* Suspend/Activate */}
                        {u.accountStatus === "suspended" ? (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-success hover:text-success"
                            title="Activate" disabled={actionLoading === u.id}
                            onClick={() => handleUpdate(u.id, { accountStatus: "active" }, "User activated")}>
                            <UserCheck className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            title="Suspend" disabled={actionLoading === u.id}
                            onClick={() => handleUpdate(u.id, { accountStatus: "suspended" }, "User suspended")}>
                            <UserX className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Promote/Demote */}
                        {u.role === "admin" ? (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            title="Demote to user" disabled={actionLoading === u.id}
                            onClick={() => handleUpdate(u.id, { role: "user" }, "Admin demoted")}>
                            <ShieldOff className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            title="Promote to admin" disabled={actionLoading === u.id}
                            onClick={() => handleUpdate(u.id, { role: "admin" }, "User promoted to admin")}>
                            <ShieldCheck className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Delete */}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          title="Delete user" disabled={actionLoading === u.id}
                          onClick={() => setDeleteUser(u)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
        {!loading && users.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">No users found.</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Page {page} of {totalPages} · {total} users</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => { setPage((p) => p - 1); load(page - 1); }}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => { setPage((p) => p + 1); load(page + 1); }}>Next</Button>
          </div>
        </div>
      )}

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) { setCreateOpen(false); setCreateEmail(""); setCreateName(""); setCreatePassword(""); setCreateRole("user"); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="create-email">Email <span className="text-destructive">*</span></Label>
              <Input id="create-email" type="email" placeholder="user@example.com"
                value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-name">Name</Label>
              <Input id="create-name" placeholder="Full name (optional)"
                value={createName} onChange={(e) => setCreateName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-password">Password <span className="text-destructive">*</span></Label>
              <Input id="create-password" type="password" placeholder="Min. 6 characters"
                value={createPassword} onChange={(e) => setCreatePassword(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-role">Role</Label>
              <select id="create-role" value={createRole}
                onChange={(e) => setCreateRole(e.target.value as "user" | "admin")}
                className="h-9 w-full rounded-md bg-background px-3 text-sm">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
            <Button onClick={handleCreateUser}
              disabled={createLoading || !createEmail || !createPassword || createPassword.length < 6}>
              {createLoading ? "Creating…" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} disabled={editLoading}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editLoading}>
              {editLoading ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={(open) => { if (!open) setDeleteUser(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteUser?.email}</strong> and cascade-delete all their
              projects, services, and deployments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteLoading}
              onClick={handleDeleteConfirm}>
              {deleteLoading ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
